/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useNotifications } from '@/components/notifications/useNotifications';
import { NotificationItem, UnreadBadge } from '@/components/notifications/NotificationItem';
import { notificationRoute } from '@/components/notifications/notificationRoute';

const NotificationsBell = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { items, unread, markAllRead, markRead } = useNotifications();

  const clickItem = async (n) => {
    await markRead(n);
    setOpen(false);
    const route = notificationRoute(n);
    if (route) navigate(route);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative h-9 w-9 rounded-full hover:bg-secondary flex items-center justify-center transition" data-testid="notifications-bell">
          <Bell className="h-5 w-5" />
          <UnreadBadge count={unread} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[340px] p-0 max-h-[70vh] overflow-hidden flex flex-col">
        <div className="px-3 py-2 flex items-center justify-between border-b border-border">
          <div className="font-display font-semibold text-sm">{t('notifications')}</div>
          {items.length > 0 && (
            <Button variant="ghost" size="sm" onClick={markAllRead} className="text-xs" data-testid="notifications-mark-all">
              <CheckCheck className="h-3.5 w-3.5 mr-1" />{t('markAllRead')}
            </Button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto scrollbar-thin">
          {items.length === 0 && (
            <div className="p-6 text-center text-sm text-muted-foreground">{t('noNotifications')}</div>
          )}
          {items.map((n) => <NotificationItem key={n.id} n={n} t={t} onClick={clickItem} />)}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationsBell;
